import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
  Button,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
// components
import {
  Log,
  sendDataWithGet,
} from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";

import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import { CustomTableCell } from "../../components/TableCell";
import { Add, Edit, Delete } from "@mui/icons-material";
import { styles } from "../../utils/styles";
import CreateQuickPayment from "./Dialogs/CreateQuickPayment";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import Label from "../../components/Label";
import DeleteQuickPayment from "./Dialogs/DeleteQuickPayment";

// ----------------------------------------------------------------------

const defaultValue = { is_active: true };

const TABLE_HEAD = [
  { id: "terminal", label: "Terminal" },
  { id: "callback_url", label: "Callback URL"},
  { id: "created_at", label: "Created At"},
//   { id: "capture", label: "Capture"},
  { id: "is_active", label: "Active" },
  { label: "" },
];

export default function QuickPayment({ userRole}) {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState(defaultValue);

  const [fLoaded, setFLoaded] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [filterCondition, setFilterCondition] = useState({});

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const renderData = (res) => {
    const newData = res.map((item) => ({
      ...item,
      terminal: item.terminal?.terminal_id,
      callback_url: item.callback_url,
      capture: item.capture,
      created_at: item.created_at,
      is_active: item.is_active,
    }));

    setTableData({
      ...res,
      results: newData,
    });
  };

  const queryPage = (params, payload = filter) => {
    loaded && setLoaded(false);
    const url = `merchant/terminal/get_quick_payment_list/`;
    sendDataWithGet(url, renderData, setLoaded);
  };

  const getFilterCondition = () => {
    const url = `merchant/terminal/filter_conditions/`;
    sendDataWithGet(url, setFilterCondition, setFLoaded);
  };

  const onFilterSubmit = (fParams) => {
    Log.info("Search Quick Payment list", fParams);
    setFilter(fParams);
    queryPage(fParams);
  };

  const handleCloseDialog = () => {
    openCreateDialog && setOpenCreateDialog(null);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    getFilterCondition();
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  return (
    <Box>
      {
        userRole?.includes("Add") && (
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            mb={2}
          >
            <Button
              variant="contained"
              onClick={() => setOpenCreateDialog(true)}
              startIcon={<Add />}
              sx={{ ...styles.customButton }}
            >
              Add Quick Payment
            </Button>
          </Stack>
        )
      }

      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row) => {
                    const {
                      id,
                      terminal,
                      callback_url,
                      is_active,
                      created_at,
                    } = row;

                    return (
                      <StyledTableRow hover key={id}>
                        <CustomTableCell field={terminal} />
                        <CustomTableCell field={callback_url} />
                        <CustomTableCell field={created_at} />
                        <CustomTableCell
                          field={
                            <Label color={is_active ? "success" : "error"}>
                              {is_active ? "YES" : "NO"}
                            </Label>
                          }
                        />
                        <CustomTableCell
                          align="right"
                          field={
                            <>
                              {
                                userRole?.includes("Delete") && (
                                  <Tooltip
                                    title={`Delete`}
                                    placement="top"
                                  >
                                    <IconButton
                                      onClick={() => {
                                        setOpenConfirmDialog({
                                          id,
                                        });
                                      }}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                )
                              }
                              {
                                userRole?.includes("Edit") && (
                                  <Tooltip title="Edit" placement="top">
                                    <IconButton
                                      onClick={() => {
                                        const url = `/merchant/terminal/get_quick_payment_details/${row.id}/`;
                                        setGlobalLoading(true);
                                        sendDataWithGet(
                                          url,
                                          setOpenCreateDialog,
                                          () => setGlobalLoading(false)
                                        );
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                )
                              }
                            </>
                          }
                        />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      ) : (
        <CircularLoader />
      )}

      {openCreateDialog && (
        <CreateQuickPayment
          data={{
            terminal: filterCondition?.terminal_id,
            details: openCreateDialog,
          }}
          onClose={handleCloseDialog}
          cb={queryPage}
        />
      )}

      {openConfirmDialog && (
        <DeleteQuickPayment
          data={openConfirmDialog}
          queryPage={queryPage}
          handleCloseDialog={() => setOpenConfirmDialog(null)}
        />
      )}
    </Box>
  );
}
